import React, { useEffect, useRef, useState } from 'react';
import { SearchResult, SourceReference } from '../../types/api';
import { CitedMarkdown, CitedReferences } from '../citations/CitedContent';
import {
  IconClock,
  IconCopy,
  IconDownload,
  IconEdit,
  IconRefresh,
  IconShare,
  IconSparkle,
} from './BriefIcons';
import { BriefLengthControl } from './BriefLengthControl';
import { countWords, describeTarget } from './briefLength';
import { BriefReferences } from './BriefReferences';
import { BriefToc } from './BriefToc';
import { BriefSelection } from './BriefSelectionMenu';
import { BUBBLE_CLASS, CommentMark, MARK_CLASS, paintCommentMarks } from './briefCommentMarks';
import {
  BriefSection,
  PROMPT_MAX_CHARS,
  SectionAuditEntry,
  SectionRunDetail,
  UnresolvedMarker,
} from './briefTypes';
import { BriefVoiceSelect, briefDefaultSuffix, voiceHint } from './BriefVoiceSelect';
import { UseBriefReturn } from './useBrief';
import { BriefDiff } from './BriefDiff';
import { BriefSectionAudit } from './BriefSectionAudit';
import { BriefRunOriginal } from './BriefRunOriginal';

const tagClass = (tag: string): string => `brief-tag brief-tag-${tag.toLowerCase()}`;

// The changes view for one audit entry: a diff header (Hide, plus Keep/Reject
// for the still-pending change) over the rendered before→after diff.
const SectionChangesView: React.FC<{
  entry: SectionAuditEntry;
  viewingPending: boolean;
  onHide: () => void;
  onKeep: () => void;
  onReject: () => void;
}> = ({ entry, viewingPending, onHide, onKeep, onReject }) => (
  <div className="brief-doc-content">
    <div className="brief-diff-head">
      <span>
        Changes from the {entry.kind === 'update' ? 'update' : 'edit'}
        {viewingPending ? '' : ` on ${new Date(entry.at).toLocaleDateString()}`}
      </span>
      <span className="brief-diff-actions">
        <button className="brief-diff-dismiss brief-diff-hide" onClick={onHide} title="Hide the diff">
          Hide changes
        </button>
        {viewingPending && (
          <>
            <button className="brief-diff-dismiss brief-diff-keep" onClick={onKeep}>
              Keep Edits
            </button>
            <button className="brief-diff-dismiss brief-diff-reject" onClick={onReject}>
              Reject Edits
            </button>
          </>
        )}
      </span>
    </div>
    <BriefDiff oldText={entry.before || ''} newText={entry.after || ''} />
  </div>
);

// The Edit/Update instruction panel (keeps the current text; runs a revise).
const AiInstructionPanel: React.FC<{
  mode: 'edit' | 'update';
  title: string;
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}> = ({ mode, title, value, onChange, onSubmit, onCancel }) => (
  <div className="brief-regen-panel brief-ai-panel">
    <div className="brief-regen-title">
      {mode === 'edit' ? `Edit “${title}”` : `Update “${title}”`}
    </div>
    <div className="brief-ai-panel-hint">
      {mode === 'edit'
        ? 'Keeps the current text and revises it to your instruction (does not replace it unless you ask).'
        : 'Searches the library for sources published since this section was last run and folds any new findings in. Add an optional focus below.'}
    </div>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      maxLength={PROMPT_MAX_CHARS}
      rows={2}
      placeholder={
        mode === 'edit'
          ? 'e.g. “Adjust so the viewpoint relates more to domestic policy”'
          : 'Optional focus for the update — e.g. “prioritise enforcement actions”'
      }
    />
    <div className="brief-regen-actions">
      <button
        className="brief-btn brief-btn-primary"
        onClick={onSubmit}
        disabled={mode === 'edit' && !value.trim()}
      >
        {mode === 'edit' ? 'Apply edit' : 'Run update'}
      </button>
      <button className="brief-btn brief-btn-secondary" onClick={onCancel}>
        Cancel
      </button>
    </div>
  </div>
);

interface SectionViewProps {
  section: BriefSection;
  num: string;
  brief: UseBriefReturn;
  onSourceClick: (source: SourceReference) => void;
  // Viewer-only (shared brief): no editing or AI actions.
  readOnly?: boolean;
  // Hover on an un-enriched citation asks for that source to be highlighted.
  onRequestHighlight?: (source: SourceReference) => void;
  // Commented passages in this section, and the thread currently open.
  marks?: CommentMark[];
  onOpenThread?: (threadId: string) => void;
  activeThreadId?: string | null;
}

// Tooltip for a section's word count: which target (if any) it was written to.
const sectionWordsTitle = (section: BriefSection, briefTarget: number | null): string => {
  const target = section.targetWords ?? briefTarget;
  return target ? `Target: about ${target} words` : 'No length target';
};

// Citations of the section the API could not match to a source: the agent
// cited a number it never returned, or returned a record without a chunk id.
// They stay visible in the text as `[?n]` and are listed in the section Log.
export const UnresolvedCitationsWarning: React.FC<{ unresolved?: UnresolvedMarker[] }> = ({
  unresolved,
}) => {
  if (!unresolved || !unresolved.length) return null;
  const markers = unresolved.map((u) => `[?${u.n}]`).join(', ');
  const noChunk = unresolved.some((u) => u.reason === 'no-chunk-id');
  return (
    <div className="brief-section-warning" role="status">
      {unresolved.length === 1 ? 'One citation' : `${unresolved.length} citations`} in this section
      could not be matched to a source and {unresolved.length === 1 ? 'is' : 'are'} shown as{' '}
      {markers}: the research agent cited {noChunk ? 'a source record with no chunk id or ' : ''}a
      number it never returned. Open the Log to see the original output.
    </div>
  );
};

// An introduction (a top-level heading with sub-headings) that kept headings of
// its own after its re-runs; see briefIntro.ts.
export const IntroHeadingWarning: React.FC<{ headings?: number }> = ({ headings }) =>
  headings ? (
    <div className="brief-section-warning" role="status">
      This introduction still has {headings} heading{headings === 1 ? '' : 's'} of its own after
      re-running. An introduction should frame its sub-sections without headings: edit them out, or
      use AI Regenerate.
    </div>
  ) : null;

// A textarea for editing a section's heading guidance / regenerating, shown for
// both pending sections (research with guidance) and done sections (re-research).

const GuidancePanel: React.FC<{
  section: BriefSection;
  brief: UseBriefReturn;
  // The inline panel for an un-researched section has nothing to cancel back to.
  hideCancel?: boolean;
}> = ({ section, brief, hideCancel }) => {
  const isPending = section.status === 'pending';
  const briefVoice = brief.voices.find((v) => v.id === brief.briefVoiceId) || null;
  const ownVoice = brief.voices.find((v) => v.id === section.voiceId) || null;
  const showVoice = brief.voices.length > 0 || !!section.voiceId || !!brief.briefVoiceId;
  return (
    <div className="brief-regen-panel">
      <div className="brief-regen-title">
        {isPending ? 'Research' : 'Regenerate'} “{section.title}”
      </div>
      <textarea
        value={section.guidance || ''}
        onChange={(e) => brief.setSectionGuidance(section.id, e.target.value)}
        maxLength={PROMPT_MAX_CHARS}
        rows={2}
        placeholder="Optional: add focus or guidance — e.g. ‘emphasise sub-Saharan Africa & 2020 onward’"
      />
      {showVoice && (
        <>
          <div className="brief-regen-voice-label">Voice &amp; tone profile</div>
          <BriefVoiceSelect
            className="brief-regen-voice-select"
            value={section.voiceId}
            voices={brief.voices}
            onChange={(id) => brief.setSectionVoiceId(section.id, id)}
            emptyLabel={`Use brief default${briefDefaultSuffix(brief.briefVoiceId, briefVoice)}`}
            ariaLabel="Voice and tone profile for this section"
          />
          <div className="brief-regen-voice-hint">
            {voiceHint(section.voiceId, ownVoice, brief.briefVoiceId, briefVoice)}
          </div>
        </>
      )}
      <div className="brief-regen-voice-label">Section length</div>
      <BriefLengthControl
        value={section.targetWords}
        onChange={(target) => brief.setSectionTargetWords(section.id, target)}
        inheritLabel={`Use brief target — ${describeTarget(brief.targetWords)}`}
        ariaLabel="Length target for this section"
      />
      <div className="brief-regen-actions">
        <button
          className="brief-btn brief-btn-primary"
          onClick={() => brief.regenerate(section.id, (section.guidance || '').trim() || null)}
        >
          {isPending ? 'Research section' : 'Re-research section'}
        </button>
        {!hideCancel && (
          <button className="brief-btn brief-btn-secondary" onClick={brief.closeRegen}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

// The AI action toolbar shown on a completed section.
const SectionTools: React.FC<{
  editing: boolean;
  auditCount: number;
  onToggleEdit: () => void;
  onRegenerate: () => void;
  onAiEdit: () => void;
  onAiUpdate: () => void;
  onOpenLog: () => void;
}> = ({ editing, auditCount, onToggleEdit, onRegenerate, onAiEdit, onAiUpdate, onOpenLog }) => (
  <div className="brief-doc-section-tools">
    <button className="brief-regen-btn" onClick={onToggleEdit}>
      <IconEdit /> {editing ? 'Done' : 'Manually Edit'}
    </button>
    <button className="brief-regen-btn" onClick={onRegenerate}>
      <IconRefresh /> AI Regenerate
    </button>
    <button
      className="brief-regen-btn"
      onClick={onAiEdit}
      title="Revise this section with an AI instruction, keeping the current content"
    >
      <IconSparkle /> AI Edit
    </button>
    <button
      className="brief-regen-btn"
      onClick={onAiUpdate}
      title="Search for sources published since this section was last run and fold them in"
    >
      <IconClock /> AI Get Updates
    </button>
    <button className="brief-section-log-link" onClick={onOpenLog}>
      Log{auditCount ? ` (${auditCount})` : ''}
    </button>
  </div>
);

// The "research this section" prompt shown for a pending section.
const SectionPending: React.FC<{ sample?: boolean; onResearch: () => void }> = ({
  sample,
  onResearch,
}) => (
  <div className="brief-pending">
    <button
      className="brief-btn brief-btn-secondary brief-research-one-btn"
      onClick={onResearch}
      disabled={sample}
      title={sample ? 'Edit this heading before researching it' : undefined}
    >
      <IconSparkle /> Research this section
    </button>
  </div>
);

// The live "researching…"/"revising…" panel; keeps the current text greyed out
// in place while a revise (Edit/Update) runs.
const SectionResearching: React.FC<{
  section: BriefSection;
  onSourceClick: (source: SourceReference) => void;
}> = ({ section, onSourceClick }) => {
  const { content, sources } = section;
  return (
    <>
      <div className="brief-researching">
        <div className="brief-researching-head">
          <span className="brief-spinner" />
          <span className="brief-researching-label">
            {section.revising ? 'Revising this section' : 'Researching this section'}
          </span>
          <span className="brief-researching-pct">{section.progress}%</span>
        </div>
        <div className="brief-activity">
          {section.activity.map((ev, idx) => (
            <div className="brief-activity-row" key={`${ev.tag}-${idx}`}>
              <span className={tagClass(ev.tag)}>{ev.tag}</span>
              <span>{ev.text}</span>
            </div>
          ))}
        </div>
      </div>
      {section.revising && !!section.content && (
        // The current text stays in place, greyed out, until the revised version
        // arrives and swaps in atomically.
        <div className="brief-doc-content brief-doc-content-stale" aria-busy="true">
          <CitedMarkdown content={content} sources={sources} onSourceClick={onSourceClick} />
        </div>
      )}
    </>
  );
};

// The completed-section body: raw-edit textarea, a change diff, or the rendered
// content — plus the collapsible evidence list.
const SectionDoneBody: React.FC<{
  section: BriefSection;
  brief: UseBriefReturn;
  editing: boolean;
  diffEntry: SectionAuditEntry | null;
  viewingPending: boolean;
  onSourceClick: (source: SourceReference) => void;
  onCloseDiff: () => void;
  onRequestHighlight?: (source: SourceReference) => void;
  marks?: CommentMark[];
  onOpenThread?: (threadId: string) => void;
  activeThreadId?: string | null;
}> = ({
  section,
  brief,
  editing,
  diffEntry,
  viewingPending,
  onSourceClick,
  onCloseDiff,
  onRequestHighlight,
  marks,
  onOpenThread,
  activeThreadId,
}) => {
  // Rendered exactly as the API returned them: the renumbered text and the
  // display sources behind its numbers.
  const { content, sources } = section;
  const proseRef = useRef<HTMLDivElement>(null);

  // Paint commented passages after the markdown renders. The marks live in the
  // DOM rather than in React's tree, so a re-render of the prose wipes them —
  // an observer puts them back. It is disconnected while painting so our own
  // mutations cannot trigger another pass.
  useEffect(() => {
    const el = proseRef.current;
    if (!el) return;
    const options = { childList: true, subtree: true, characterData: true };
    const observer = new MutationObserver(() => {
      if (!el.querySelector(`.${MARK_CLASS}`) && (marks || []).length) repaint();
    });
    function repaint(): void {
      observer.disconnect();
      paintCommentMarks(el as HTMLElement, marks || []);
      observer.observe(el as HTMLElement, options);
    }
    repaint();
    return () => observer.disconnect();
  }, [content, marks]);

  // Show which passage the open thread belongs to.
  useEffect(() => {
    const el = proseRef.current;
    if (!el) return;
    el.querySelectorAll(`.${MARK_CLASS}`).forEach((m) => {
      m.classList.toggle(
        'brief-comment-mark-active',
        !!activeThreadId && m.getAttribute('data-thread-id') === activeThreadId,
      );
    });
  }, [activeThreadId, marks, content]);

  return (
    <>
      {editing ? (
        <textarea
          className="brief-edit-textarea"
          value={section.content}
          onChange={(e) => brief.editContent(section.id, e.target.value)}
          // Leaving the editor records the edit as a run of the section.
          onBlur={() => void brief.commitManualEdit(section.id)}
          rows={Math.min(28, Math.max(8, section.content.split('\n').length + 2))}
        />
      ) : diffEntry && diffEntry.before != null ? (
        <SectionChangesView
          entry={diffEntry}
          viewingPending={viewingPending}
          onHide={onCloseDiff}
          onKeep={() => {
            onCloseDiff();
            brief.dismissChanges(section.id);
          }}
          onReject={() => {
            onCloseDiff();
            brief.rejectChanges(section.id);
          }}
        />
      ) : (
        <div
          className="brief-doc-content"
          ref={proseRef}
          onClick={(e) => {
            const bubble = (e.target as HTMLElement).closest(`.${BUBBLE_CLASS}`);
            const threadId = bubble?.getAttribute('data-thread-id');
            if (threadId && onOpenThread) onOpenThread(threadId);
          }}
        >
          <CitedMarkdown
            content={content}
            sources={sources}
            onSourceClick={onSourceClick}
            onRequestHighlight={onRequestHighlight}
          />
        </div>
      )}
      <CitedReferences
        content={content}
        sources={sources}
        onSourceClick={onSourceClick}
        collapsible
        labelPrefix="Evidence"
        className="brief-evidence"
        hidePages={brief.referenceGrouping === 'document-single'}
      />
    </>
  );
};

// Load a section's stored original (the agent's text, sources and trace)
// when the Log asks for one; a stale load never lands on a newer request.
const useRunOriginal = (
  brief: UseBriefReturn,
  sectionId: string,
  runId: string | null,
): { original: SectionRunDetail | null; originalError: string | null } => {
  const [original, setOriginal] = useState<SectionRunDetail | null>(null);
  const [originalError, setOriginalError] = useState<string | null>(null);
  const { getSectionRunDetail } = brief;
  useEffect(() => {
    if (!runId) return undefined;
    let cancelled = false;
    setOriginal(null);
    setOriginalError(null);
    getSectionRunDetail(sectionId, runId)
      .then((detail) => {
        if (!cancelled) setOriginal(detail);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setOriginalError(e instanceof Error ? e.message : 'Could not load the original.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [getSectionRunDetail, sectionId, runId]);
  return { original, originalError };
};

// How an un-researched section invites research: inline panel (so instructions
// can be written for several sections before one "Regenerate all" run), the
// legacy button for un-edited sample headings, or nothing while busy/read-only.
type PendingMode = 'none' | 'inline' | 'button';

const pendingResearchMode = (
  section: BriefSection,
  panelOpen: boolean,
  anyResearching: boolean,
  readOnly: boolean,
): PendingMode => {
  if (section.status !== 'pending' || panelOpen || anyResearching || readOnly) return 'none';
  return section.sample ? 'button' : 'inline';
};

// Which audit entry's diff is on screen, and whether it is the still-pending
// change: the latest entry while an AI edit/update awaits Keep/Reject (only it
// offers those buttons; everything else is view-only).
const diffState = (
  section: BriefSection,
  audit: SectionAuditEntry[],
  diffEntryId: string | null,
): {
  pendingEntryId: string | null;
  diffEntry: SectionAuditEntry | null;
  viewingPending: boolean;
} => {
  const pendingEntryId =
    section.pendingRunId && audit.length ? audit[audit.length - 1].id : null;
  const diffEntry = diffEntryId ? audit.find((e) => e.id === diffEntryId) ?? null : null;
  return { pendingEntryId, diffEntry, viewingPending: !!diffEntryId && diffEntryId === pendingEntryId };
};

const BriefSectionView: React.FC<SectionViewProps> = ({
  section,
  num,
  brief,
  onSourceClick,
  readOnly = false,
  onRequestHighlight,
  marks,
  onOpenThread,
  activeThreadId,
}) => {
  const [editing, setEditing] = useState(false);
  // The stored original being viewed from the Log, and its load state.
  const [originalRunId, setOriginalRunId] = useState<string | null>(null);
  const { original, originalError } = useRunOriginal(brief, section.id, originalRunId);
  // AI Edit/Update instruction panel + audit modal + changes toggle.
  const [aiPanel, setAiPanel] = useState<null | 'edit' | 'update'>(null);
  const [aiText, setAiText] = useState('');
  const [logOpen, setLogOpen] = useState(false);
  // Which audit entry's diff is on screen (by id), or null. Any edit/update
  // entry with a stored before/after can be viewed — including on a reloaded
  // brief — not just the latest pending change.
  const [diffEntryId, setDiffEntryId] = useState<string | null>(null);
  const panelOpen = brief.regenFor === section.id;
  const isDone = section.status === 'done';
  const anyResearching = brief.sections.some((s) => s.status === 'researching');
  const pendingMode = pendingResearchMode(section, panelOpen, anyResearching, !!readOnly);
  const audit = section.audit ?? [];
  const auditCount = audit.length;
  const { pendingEntryId, diffEntry, viewingPending } = diffState(section, audit, diffEntryId);
  // Surface the pending diff automatically once an edit/update completes (a new
  // pending entry appears).
  useEffect(() => {
    if (pendingEntryId) setDiffEntryId(pendingEntryId);
  }, [pendingEntryId]);
  const submitAi = () => {
    const panel = aiPanel;
    if (!panel) return;
    setAiPanel(null);
    setDiffEntryId(null);
    brief.reviseSection(section.id, panel, aiText.trim() || null);
    setAiText('');
  };

  return (
    <section
      id={`brief-section-${section.id}`}
      data-brief-section-id={section.id}
      className={`brief-doc-section${section.level === 2 ? ' brief-doc-section-sub' : ''}`}
    >
      <div className="brief-doc-section-head">
        {num && <span className="brief-doc-section-num">{num}</span>}
        <input
          className="brief-doc-section-title"
          value={section.title}
          onChange={(e) => brief.editTitle(section.id, e.target.value)}
          onBlur={brief.commitEdits}
          readOnly={readOnly}
        />
        {isDone && (
          <span className="brief-doc-section-words" title={sectionWordsTitle(section, brief.targetWords)}>
            {countWords(section.content).toLocaleString()} words
          </span>
        )}
      </div>

      {isDone && !readOnly && (
        <SectionTools
          editing={editing}
          auditCount={auditCount}
          onToggleEdit={() => setEditing((v) => !v)}
          onRegenerate={() => brief.openRegen(section.id)}
          onAiEdit={() => {
            setAiText('');
            setAiPanel((p) => (p === 'edit' ? null : 'edit'));
          }}
          onAiUpdate={() => {
            setAiText('');
            setAiPanel((p) => (p === 'update' ? null : 'update'));
          }}
          onOpenLog={() => setLogOpen(true)}
        />
      )}

      <IntroHeadingWarning headings={isDone ? section.introHeadingWarning : undefined} />
      {isDone && <UnresolvedCitationsWarning unresolved={section.unresolved} />}

      {aiPanel && (
        <AiInstructionPanel
          mode={aiPanel}
          title={section.title}
          value={aiText}
          onChange={setAiText}
          onSubmit={submitAi}
          onCancel={() => {
            setAiPanel(null);
            setAiText('');
          }}
        />
      )}

      {logOpen && (
        <BriefSectionAudit
          title={section.title}
          audit={audit}
          pendingEntryId={pendingEntryId}
          // Any edit/update entry with a stored before/after can be re-viewed —
          // clicking its row opens that change's diff.
          onShowChanges={(entryId) => {
            setLogOpen(false);
            setDiffEntryId(entryId);
          }}
          runs={section.runs || []}
          onViewOriginal={(runId) => {
            setLogOpen(false);
            setOriginalRunId(runId);
          }}
          onClose={() => setLogOpen(false)}
        />
      )}

      {originalRunId && (
        <BriefRunOriginal
          title={section.title}
          detail={original}
          error={originalError}
          onClose={() => setOriginalRunId(null)}
        />
      )}

      {(panelOpen || pendingMode === 'inline') && (
        <GuidancePanel section={section} brief={brief} hideCancel={pendingMode === 'inline'} />
      )}

      {pendingMode === 'button' && (
        <SectionPending sample onResearch={() => brief.openRegen(section.id)} />
      )}

      {section.status === 'researching' && (
        <SectionResearching section={section} onSourceClick={onSourceClick} />
      )}

      {isDone && (
        <SectionDoneBody
          section={section}
          brief={brief}
          editing={editing}
          diffEntry={diffEntry}
          viewingPending={viewingPending}
          onSourceClick={onSourceClick}
          onCloseDiff={() => setDiffEntryId(null)}
          onRequestHighlight={onRequestHighlight}
          marks={marks}
          onOpenThread={onOpenThread}
          activeThreadId={activeThreadId}
        />
      )}
    </section>
  );
};

// "Export to Word": one button. The document mirrors the on-screen
// references, so its layout follows the "Group by document" toggles.
const ExportButton: React.FC<{
  disabled: boolean;
  busy: boolean;
  onExportWord: () => void;
}> = ({ disabled, busy, onExportWord }) => (
  <button
    className="brief-export-word"
    onClick={onExportWord}
    disabled={disabled || busy}
    title="Export this brief to Word, with citations linked to the source documents"
  >
    {busy ? 'Exporting…' : <><IconDownload /> Export to Word</>}
  </button>
);

// The document-level action row: regenerate/update all, share, save-as-template
// and the Word export menu. Owner-only actions are hidden for viewers.
const HeaderActions: React.FC<{
  brief: UseBriefReturn;
  readOnly: boolean;
  canEditStructure: boolean;
  onOpenShare?: () => void;
  onSaveTemplate?: () => void;
  onRegenerateAll?: () => void;
  onExportWord?: () => void;
  exportBusy?: boolean;
}> = ({
  brief,
  readOnly,
  canEditStructure,
  onOpenShare,
  onSaveTemplate,
  onRegenerateAll,
  onExportWord,
  exportBusy,
}) => {
  const { sections } = brief;
  const hasSections = sections.length > 0;
  return (
    <div className="brief-doc-header-actions">
      {!readOnly && hasSections && (
        <button
          className="brief-regen-all"
          onClick={onRegenerateAll || (() => void brief.startResearch())}
          disabled={!canEditStructure}
          title="Re-research every section from scratch"
        >
          <IconRefresh /> AI Regenerate All
        </button>
      )}
      {!readOnly && sections.some((s) => s.status === 'done') && (
        <button
          className="brief-regen-all"
          onClick={brief.updateAll}
          disabled={!canEditStructure}
          title="Fold sources published since each section was last run into every section"
        >
          <IconClock /> AI Get All Recent Updates
        </button>
      )}
      {!readOnly && onOpenShare && (
        <button
          className="brief-regen-all"
          onClick={onOpenShare}
          title="Share this brief (viewer-only) with people or groups"
        >
          <IconShare /> Share
        </button>
      )}
      {!readOnly && onSaveTemplate && hasSections && (
        <button
          className="brief-regen-all"
          onClick={onSaveTemplate}
          title="Save this brief's headings as a reusable template"
        >
          <IconCopy /> Save as Template
        </button>
      )}
      {onExportWord && (
        <ExportButton
          disabled={!hasSections}
          busy={!!exportBusy}
          onExportWord={onExportWord}
        />
      )}
    </div>
  );
};

interface BriefDocumentProps {
  brief: UseBriefReturn;
  onResultClick?: (result: SearchResult) => void;
  // Export the brief to Word. 'links' keeps inline [n] citations; 'footnotes'
  // renders each citation as a Word footnote on the relevant page.
  onExportWord?: () => void;
  exportBusy?: boolean;
  // Brief Central integrations (remote mode only).
  onOpenShare?: () => void;
  onSaveTemplate?: () => void;
  onRegenerateAll?: () => void;
  // False when the Contents panel renders in the workspace side rail instead
  // (logged-in layout); true keeps the inline panel (anonymous layout).
  showToc?: boolean;
  // Selecting text inside a section surfaces the selection toolbar; the owner
  // decides which actions to offer.
  onSelectText?: (selection: BriefSelection) => void;
  // Commented passages to paint into the prose, keyed by section id.
  commentMarks?: Map<string, CommentMark[]>;
  // A speech bubble in the text was clicked.
  onOpenThread?: (threadId: string) => void;
  // Comment visibility, and the control for it shown in the meta row.
  showComments?: boolean;
  onToggleComments?: (show: boolean) => void;
  // The thread being viewed, so its passage can be shown as active.
  activeThreadId?: string | null;
}

// Floating bar fixed to the bottom of the viewport while research runs: which
// section is being written, overall progress, and a Stop control.
const ResearchStatusBar: React.FC<{ brief: UseBriefReturn }> = ({ brief }) => {
  const current = brief.sections.find((s) => s.status === 'researching');
  if (!current) return null;
  const total = brief.sections.length;
  return (
    <div className="brief-status-bar" role="status">
      <span className="brief-spinner brief-status-bar-spinner" />
      <div className="brief-status-bar-main">
        <div className="brief-status-bar-label">
          {current.revising ? 'Revising' : 'Researching'} “{current.title}”
          <span className="brief-status-bar-count">
            {brief.doneCount}/{total} sections
          </span>
        </div>
        <div className="brief-status-bar-track">
          <div className="brief-status-bar-fill" style={{ width: `${brief.totalProgress}%` }} />
        </div>
      </div>
      <button
        className="brief-status-bar-stop"
        onClick={brief.stopResearch}
        title="Stop all research"
      >
        ■ Stop
      </button>
    </div>
  );
};

const autoSizeTitle = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

// The selection the reader just made inside a section, or null. The section
// is resolved from the DOM so nested markup (links, citations) still traces
// back to it.
const selectionInSection = (): BriefSelection | null => {
  const sel = window.getSelection();
  const text = sel ? sel.toString().trim() : '';
  if (!sel || !text || text.length < 3 || sel.rangeCount !== 1) return null;
  const node = sel.anchorNode;
  const el = node instanceof HTMLElement ? node : (node?.parentElement as HTMLElement | null);
  const sectionEl = el?.closest('[data-brief-section-id]') as HTMLElement | null;
  const sectionId = sectionEl?.getAttribute('data-brief-section-id');
  if (!sectionId) return null;
  const range = sel.getRangeAt(0);
  const box = range.getBoundingClientRect();
  // One rect per line, so a multi-line selection stays fully marked.
  const rects = Array.from(range.getClientRects()).map((r) => ({
    top: r.top,
    left: r.left,
    width: r.width,
    height: r.height,
  }));
  return { sectionId, text, rect: { top: box.top, left: box.left, width: box.width }, rects };
};

// The line under the title: counts, the viewer chip, the outline log toggle
// and the comments switch.
const BriefMeta: React.FC<{
  brief: UseBriefReturn;
  readOnly: boolean;
  hasOutlineLog: boolean;
  logOpen: boolean;
  onToggleLog: () => void;
  showComments?: boolean;
  onToggleComments?: (show: boolean) => void;
}> = ({ brief, readOnly, hasOutlineLog, logOpen, onToggleLog, showComments, onToggleComments }) => (
  <div className="brief-doc-meta">
    <span>{brief.sections.length} sections</span>
    <span>·</span>
    <span>{brief.totalSources} sources synthesised</span>
    {brief.totalWords > 0 && (
      <>
        <span>·</span>
        <span>{brief.totalWords.toLocaleString()} words</span>
      </>
    )}
    {readOnly && brief.ownerName && (
      <>
        <span>·</span>
        <span className="brief-viewer-chip">Owned by {brief.ownerName} — view only</span>
      </>
    )}
    {hasOutlineLog && (
      <>
        <span>·</span>
        <button className="brief-meta-link" onClick={onToggleLog} aria-expanded={logOpen}>
          Outline analysis
        </button>
      </>
    )}
    {onToggleComments && brief.sections.length > 0 && (
      <label className="brief-comments-visibility" title="Show or hide comments on this brief">
        <input
          type="checkbox"
          checked={showComments !== false}
          onChange={(e) => onToggleComments(e.target.checked)}
        />
        Show comments
      </label>
    )}
  </div>
);

// The document-wide progress row shown while research runs.
const ResearchProgress: React.FC<{ brief: UseBriefReturn }> = ({ brief }) => (
  <div className="brief-doc-progress">
    <div className="brief-doc-progress-row">
      <span>Researching…</span>
      <span className="brief-doc-progress-right">
        {brief.doneCount}/{brief.sections.length}
        <button className="brief-stop-btn" onClick={brief.stopResearch} title="Stop all research">
          ■ Stop
        </button>
      </span>
    </div>
    <div className="brief-progress-track">
      <div className="brief-progress-fill" style={{ width: `${brief.totalProgress}%` }} />
    </div>
  </div>
);

// The outline-generation activity log (queries run, sources read).
const OutlineLog: React.FC<{ brief: UseBriefReturn; onClose: () => void }> = ({ brief, onClose }) => (
  <div className="brief-outline-log">
    <div className="brief-outline-log-head">
      <span>Outline research — queries run and sources read</span>
      <button
        className="brief-outline-log-close"
        onClick={onClose}
        title="Close"
        aria-label="Close outline analysis"
      >
        ×
      </button>
    </div>
    <div className="brief-activity">
      {brief.generatingActivity.map((ev, i) => (
        <div className="brief-activity-row" key={`${ev.tag}-${i}`}>
          <span className={tagClass(ev.tag)}>{ev.tag}</span>
          <span>{ev.text}</span>
        </div>
      ))}
    </div>
  </div>
);

export const BriefDocument: React.FC<BriefDocumentProps> = ({
  brief,
  onResultClick,
  onExportWord,
  exportBusy,
  onOpenShare,
  onSaveTemplate,
  onRegenerateAll,
  showToc = true,
  onSelectText,
  commentMarks,
  onOpenThread,
  activeThreadId,
  showComments,
  onToggleComments,
}) => {
  const { sections, numbers } = brief;
  const [logOpen, setLogOpen] = useState(false);
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const hasOutlineLog = brief.generatingActivity.length > 0;

  useEffect(() => autoSizeTitle(titleRef.current), [brief.briefTitle]);

  // Viewer-only: a brief shared with (not owned by) this user renders read-only.
  const readOnly = !brief.canEdit;
  // Structural edits (reorder/add/remove via the TOC) lock while research runs.
  const canEditStructure = !readOnly && !sections.some((s) => s.status === 'researching');

  // Convert an assistant SourceReference into the SearchResult the app's
  // document preview expects (same mapping the Research Assistant uses).
  const handleSourceClick = (source: SourceReference) => {
    if (!onResultClick) return;
    onResultClick({
      chunk_id: source.chunkId,
      doc_id: source.docId,
      title: source.title,
      text: source.text,
      page_num: source.page || 1,
      score: source.score,
      headings: source.headings || [],
      bbox: source.bbox,
      metadata: {},
    });
  };

  // Report a selection made inside a section (comments anchor to it).
  const handleMouseUp = () => {
    if (!onSelectText) return;
    const selection = selectionInSection();
    if (selection) onSelectText(selection);
  };

  return (
    <div className="brief-doc" onMouseUp={handleMouseUp}>
      <div className="brief-doc-header">
        <div className="brief-doc-header-top">
          <div className="brief-eyebrow">EVIDENCE BRIEF</div>
          <HeaderActions
            brief={brief}
            readOnly={readOnly}
            canEditStructure={canEditStructure}
            onOpenShare={onOpenShare}
            onSaveTemplate={onSaveTemplate}
            onRegenerateAll={onRegenerateAll}
            onExportWord={onExportWord}
            exportBusy={exportBusy}
          />
        </div>
        <textarea
          ref={titleRef}
          className="brief-doc-title-input"
          value={brief.briefTitle}
          onChange={(e) => {
            brief.setBriefTitle(e.target.value);
            autoSizeTitle(e.target);
          }}
          onBlur={brief.commitEdits}
          rows={1}
          aria-label="Brief title"
          readOnly={readOnly}
        />
        <BriefMeta
          brief={brief}
          readOnly={readOnly}
          hasOutlineLog={hasOutlineLog}
          logOpen={logOpen}
          onToggleLog={() => setLogOpen((v) => !v)}
          showComments={showComments}
          onToggleComments={onToggleComments}
        />
        {brief.stage === 'research' && <ResearchProgress brief={brief} />}
        {logOpen && hasOutlineLog && <OutlineLog brief={brief} onClose={() => setLogOpen(false)} />}
      </div>

      {showToc && sections.length > 0 && <BriefToc brief={brief} canEdit={canEditStructure} />}

      {sections.map((s, i) => (
        <BriefSectionView
          key={s.id}
          section={s}
          num={brief.numberHeadings ? numbers[i] : ''}
          brief={brief}
          onSourceClick={handleSourceClick}
          readOnly={readOnly}
          onRequestHighlight={(src) =>
            src.chunkId && brief.requestSourceHighlight(s.id, src.chunkId)
          }
          marks={showComments === false ? undefined : commentMarks?.get(s.id)}
          onOpenThread={onOpenThread}
          activeThreadId={activeThreadId}
        />
      ))}

      {brief.stage === 'outline' && !readOnly && (
        <div className="brief-doc-actions">
          <button
            className="brief-btn brief-btn-primary"
            onClick={onRegenerateAll || (() => void brief.startResearch())}
            disabled={!sections.length}
          >
            Start deep research →
          </button>
        </div>
      )}

      <BriefReferences
        references={brief.references}
        groups={brief.referenceGroups}
        grouping={brief.referenceGrouping}
        onGroupingChange={brief.setReferenceGrouping}
        onSourceClick={handleSourceClick}
        wordLinkTarget={brief.wordLinkTarget}
        onWordLinkTargetChange={brief.setWordLinkTarget}
      />

      <ResearchStatusBar brief={brief} />
    </div>
  );
};
